/**
 * CART MANAGER - Panier avec taxes d'accise réconciliées
 *
 * Contenu du panier : /cart.js. Taxes attendues par produit : metafields, lus côté
 * serveur par la section `cart-tax-expected`.
 * Chaque produit qui porte une accise a sa propre ligne de taxe, de même quantité,
 * rattachée par les propriétés « Applicable sur » (lisible dans l'admin) et _tax_for.
 *
 * La réconciliation tourne :
 * - dans add() / update(), tout de suite après le changement
 * - après toute requête /cart/add|change|update faite ailleurs (thème, apps)
 * - au chargement de la page
 * - au clic sur un bouton de checkout (dernier contrôle avant de quitter le panier)
 */

window.CartManager = (function () {
  "use strict";

  // === CONFIGURATION ===
  let DEBUG = false; // Désactivé en production - activer avec CartManager.setDebug(true)

  // true  : si les taxes ne peuvent pas être corrigées, le checkout est bloqué avec un message
  // false : on laisse passer (l'écart est seulement loggé dans la console)
  const BLOCK_CHECKOUT_ON_MISMATCH = true;

  const EXPECTED_SECTION = "cart-tax-expected";
  const MAX_PASSES = 3;
  const REQUEST_TIMEOUT_MS = 10000;
  const CHECKOUT_TIMEOUT_MS = 15000;
  const CHECKOUT_PASS_MS = 5000;
  const CHECKOUT_SELECTOR = [
    '[name="checkout"]',
    '[name="goto_pp"]',
    '[name="goto_gc"]',
    'a[href*="/checkout"]',
    ".wcp_checkout_btn",
    "#wcp-30-open",
  ].join(",");

  // Nos propres requêtes passent par le fetch d'origine : elles ne redéclenchent
  // pas la réconciliation automatique (voir watchCartRequests)
  const nativeFetch = window.fetch.bind(window);
  const root = window.Shopify?.routes?.root || "/";

  function ensureJsonUrl(url, fallback) {
    const resolved = url || fallback;
    if (!resolved) return fallback;
    return resolved.endsWith(".js") ? resolved : `${resolved}.js`;
  }

  const routes = {
    add: ensureJsonUrl(window.routes?.cart_add_url, "/cart/add.js"),
    update: ensureJsonUrl(window.routes?.cart_update_url, "/cart/update.js"),
    get: ensureJsonUrl(window.routes?.cart_url, "/cart.js"),
    clear: ensureJsonUrl(window.routes?.cart_clear_url, "/cart/clear.js"),
  };

  // === LOGS ===
  function log(...args) {
    if (DEBUG) console.log("[Cart]", ...args);
  }

  function logError(...args) {
    console.error("[Cart]", ...args);
  }

  function t(fr, en) {
    const lang = (document.documentElement.lang || "fr").toLowerCase();
    return lang.startsWith("fr") ? fr : en;
  }

  // === REQUÊTES ===
  async function request(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const response = await nativeFetch(url, {
        credentials: "same-origin",
        ...options,
        signal: controller.signal,
      });
      const text = await response.text();
      let data = null;
      try {
        data = JSON.parse(text);
      } catch (e) {
        /* réponse non-JSON */
      }
      return { ok: response.ok, status: response.status, data };
    } finally {
      clearTimeout(timer);
    }
  }

  function postJson(url, body) {
    return request(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    });
  }

  function errorMessage(result, fallback) {
    return result?.data?.description || result?.data?.message || fallback;
  }

  // Toutes les opérations sur le panier passent une à la fois
  let queue = Promise.resolve();
  function enqueue(task) {
    const run = queue.then(task, task);
    queue = run.catch(() => {});
    return run;
  }

  // === RÉCUPÉRER LE PANIER ===
  async function getCart() {
    const result = await request(routes.get);
    if (!result.data) throw new Error("Panier illisible");
    return result.data;
  }

  // === SECTIONS À METTRE À JOUR ===
  function getSections() {
    const sections = ["cart-icon-bubble"];

    if (document.querySelector("cart-drawer")) {
      sections.push("cart-drawer");
    }
    if (document.querySelector("cart-notification")) {
      sections.push("cart-notification-product");
      sections.push("cart-notification-button");
    }

    const mainCartItems = document.getElementById("main-cart-items");
    if (mainCartItems?.dataset.id) {
      sections.push(mainCartItems.dataset.id);
    }

    const mainCartFooter = document.getElementById("main-cart-footer");
    if (mainCartFooter?.dataset.id) {
      sections.push(mainCartFooter.dataset.id);
    }

    return sections;
  }

  async function fetchSections() {
    const result = await request(
      `${window.location.pathname}?sections=${getSections().join(",")}`,
    );
    return result.data ? { sections: result.data } : null;
  }

  // === ÉTAT DES TAXES ===
  // Ce qui est dans le panier vient de /cart.js (toujours exact).
  // Les taxes attendues par variante viennent des metafields, via la section cart-tax-expected.
  const taxMap = new Map(); // variante -> { tax, fee, prov, fed }

  async function loadTaxMap() {
    const result = await request(
      `${root}?sections=${EXPECTED_SECTION}&_=${Date.now()}`,
      { cache: "no-store" },
    );
    const node = new DOMParser()
      .parseFromString(result.data?.[EXPECTED_SECTION] || "", "text/html")
      .getElementById(EXPECTED_SECTION);
    let lines = null;
    try {
      lines = JSON.parse(node.textContent).lines;
    } catch (e) {
      /* section absente ou illisible */
    }
    if (!Array.isArray(lines)) throw new Error("Taxes attendues illisibles");
    lines.forEach((line) => taxMap.set(line.variant_id, line));
  }

  // Propriétés d'une ligne de taxe : le lien avec son produit est _tax_for (variante),
  // « Applicable sur » est la version lisible sur la commande
  function taxProperties(entry) {
    return {
      "Applicable sur": entry.label,
      _is_tax_product: "true",
      _tax_type: entry.type,
      _tax_for: String(entry.forId),
    };
  }

  function productLabel(item) {
    const variant =
      item.variant_title && item.variant_title !== "Default Title"
        ? ` - ${item.variant_title}`
        : "";
    const sku = item.sku ? ` (${item.sku})` : "";
    return `${item.product_title || item.title || "Produit"}${variant}${sku}`;
  }

  function hasTaxProperties(item) {
    const properties = item.properties || {};
    return (
      properties._is_tax_product === "true" ||
      properties._tax_type === "provincial" ||
      properties._tax_type === "federal"
    );
  }

  // Une ligne par item du panier :
  // { key, variant_id, qty, tax, fee, prov, fed, taxFor, label }
  async function buildState(cart) {
    const items = cart.items || [];
    const hasUnknown = () =>
      items.some(
        (item) => !hasTaxProperties(item) && !taxMap.has(item.variant_id),
      );

    if (hasUnknown()) await loadTaxMap();
    if (hasUnknown()) {
      await new Promise((resolve) => setTimeout(resolve, 500));
      await loadTaxMap();
    }
    if (hasUnknown()) {
      throw new Error("Taxes attendues inconnues pour un produit du panier");
    }

    // Toute variante désignée comme taxe par un produit du panier est une ligne de taxe,
    // même sans propriété ni tag
    const taxVariants = new Set();
    items.forEach((item) => {
      const info = taxMap.get(item.variant_id);
      if (hasTaxProperties(item) || !info || info.tax || info.fee) return;
      if (info.prov) taxVariants.add(info.prov);
      if (info.fed) taxVariants.add(info.fed);
    });

    return items.map((item) => {
      const info = taxMap.get(item.variant_id) || {};
      const tax =
        hasTaxProperties(item) ||
        !!info.tax ||
        taxVariants.has(item.variant_id);
      const fee = !tax && !!info.fee;
      const taxed = !tax && !fee;
      return {
        key: item.key,
        variant_id: item.variant_id,
        qty: item.quantity,
        tax,
        fee,
        prov: taxed ? info.prov || null : null,
        fed: taxed ? info.fed || null : null,
        // Ligne de taxe : variante du produit auquel elle est rattachée
        taxFor: tax ? String(item.properties?._tax_for || "") : null,
        label: productLabel(item),
      };
    });
  }

  async function fetchState() {
    return buildState(await getCart());
  }

  function isCart(data) {
    return !!data && Array.isArray(data.items) && "item_count" in data;
  }

  // Compare les taxes présentes aux taxes attendues et retourne les corrections
  function computeFix(state) {
    const pairId = (taxId, forId) => `${taxId}|${forId}`;

    const expected = new Map(); // taxe|produit -> { taxId, forId, type, qty, label }
    const expect = (taxId, type, line) => {
      if (!taxId) return;
      const id = pairId(taxId, line.variant_id);
      const entry = expected.get(id) || {
        taxId,
        forId: line.variant_id,
        type,
        qty: 0,
        label: line.label,
      };
      entry.qty += line.qty;
      expected.set(id, entry);
    };
    state.forEach((line) => {
      if (line.tax || line.fee) return;
      expect(line.prov, "provincial", line);
      expect(line.fed, "federal", line);
    });

    // Une ligne de taxe sans produit rattaché (ancien format) ne correspond à aucune
    // paire attendue : elle est retirée et remplacée par une ligne rattachée
    const present = new Map(); // taxe|produit -> lignes du panier
    state.forEach((line) => {
      if (!line.tax) return;
      const id = pairId(line.variant_id, line.taxFor);
      if (!present.has(id)) present.set(id, []);
      present.get(id).push(line);
    });

    const updates = {};
    const adds = [];
    new Set([...expected.keys(), ...present.keys()]).forEach((id) => {
      const entry = expected.get(id);
      const target = entry?.qty || 0;
      const lines = present.get(id) || [];

      if (lines.length === 0) {
        if (target > 0) {
          adds.push({
            id: entry.taxId,
            quantity: target,
            properties: taxProperties(entry),
          });
        }
        return;
      }

      // Une seule ligne de taxe par produit
      lines.forEach((line, index) => {
        const quantity = index === 0 ? target : 0;
        if (line.qty !== quantity) updates[line.key] = quantity;
      });
    });

    return {
      expected,
      updates,
      adds,
      clean: Object.keys(updates).length === 0 && adds.length === 0,
    };
  }

  // Applique les corrections jusqu'à ce que le panier relu soit conforme
  async function reconcileState(initialState) {
    let state = initialState || (await fetchState());
    let changed = false;
    let data = null;
    let error = null;

    for (let pass = 0; pass < MAX_PASSES; pass++) {
      const fix = computeFix(state);
      if (fix.clean) return { ok: true, changed, state, data };

      log("Correction des taxes", { updates: fix.updates, adds: fix.adds });
      changed = true;
      data = null;
      let cart = null;
      const sections = getSections();
      const sections_url = window.location.pathname;

      if (Object.keys(fix.updates).length > 0) {
        const result = await postJson(routes.update, {
          updates: fix.updates,
          sections,
          sections_url,
        });
        if (result.ok && isCart(result.data)) {
          data = result.data;
          cart = result.data;
        } else {
          error = errorMessage(result, "Erreur mise à jour des taxes");
        }
      }

      let addFailed = false;
      if (fix.adds.length > 0) {
        const result = await postJson(routes.add, {
          items: fix.adds,
          sections,
          sections_url,
        });
        cart = null;
        if (result.ok) {
          data = { sections: result.data?.sections };
        } else {
          addFailed = true;
          data = null;
          error = errorMessage(result, "Erreur ajout des taxes");
        }
      }

      // Toujours vérifier sur le panier réel, jamais sur ce qu'on pense avoir envoyé
      state = await buildState(cart || (await getCart()));
      // Un ajout refusé (taxe indisponible, etc.) ne passera pas mieux au prochain essai
      if (addFailed) break;
    }

    const remaining = computeFix(state);
    if (!remaining.clean) {
      logError(
        "[Accise] Écart non corrigé " +
          JSON.stringify({
            error,
            updates: remaining.updates,
            adds: remaining.adds,
            state,
          }),
      );
    }
    return { ok: remaining.clean, changed, state, data, error };
  }

  // === RENDU DU PANIER ===
  function renderCart(data, openDrawer = false) {
    const sections = data.sections || {};

    // Cart drawer
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer) {
      if (sections["cart-drawer"]) {
        const html = new DOMParser()
          .parseFromString(sections["cart-drawer"], "text/html")
          .querySelector("#CartDrawer")?.innerHTML;

        if (html) {
          cartDrawer.querySelector("#CartDrawer").innerHTML = html;
        }
      }

      cartDrawer.classList.toggle("is-empty", data.item_count === 0);

      if (openDrawer && typeof cartDrawer.open === "function") {
        cartDrawer.open();
      }
    }

    // Cart notification
    const cartNotification = document.querySelector("cart-notification");
    if (cartNotification && sections["cart-notification-product"]) {
      const el = cartNotification.querySelector(
        '[id^="cart-notification-product"]',
      );
      if (el) {
        el.innerHTML =
          new DOMParser()
            .parseFromString(sections["cart-notification-product"], "text/html")
            .querySelector('[id^="cart-notification-product"]')?.innerHTML ||
          "";
      }
    }

    // Cart icon bubble
    document.querySelectorAll(".cart-count-bubble span").forEach((el) => {
      el.textContent = data.item_count;
    });
    document.querySelectorAll(".cart-count-bubble").forEach((el) => {
      el.style.display = data.item_count > 0 ? "" : "none";
    });

    // Gérer l'état "panier vide"
    const isEmpty = data.item_count === 0;

    // Main cart items - la classe is-empty est sur <cart-items>
    const mainCartItems = document.getElementById("main-cart-items");
    const cartItemsElement = document.querySelector("cart-items");

    if (cartItemsElement) {
      cartItemsElement.classList.toggle("is-empty", isEmpty);
    }

    if (mainCartItems && sections[mainCartItems.dataset.id]) {
      const newContent = new DOMParser()
        .parseFromString(sections[mainCartItems.dataset.id], "text/html")
        .querySelector(".js-contents")?.innerHTML;

      const target = mainCartItems.querySelector(".js-contents");
      if (target && newContent) {
        target.innerHTML = newContent;
      }
    }

    // Main cart footer - la classe is-empty est directement sur #main-cart-footer
    const mainCartFooter = document.getElementById("main-cart-footer");
    if (mainCartFooter) {
      mainCartFooter.classList.toggle("is-empty", isEmpty);

      if (sections[mainCartFooter.dataset.id]) {
        const newContent = new DOMParser()
          .parseFromString(sections[mainCartFooter.dataset.id], "text/html")
          .querySelector(".js-contents")?.innerHTML;

        const target = mainCartFooter.querySelector(".js-contents");
        if (target && newContent) {
          target.innerHTML = newContent;
        }
      }
    }

    // Réinitialiser le slider upsell si présent
    if (typeof window.initUpsellSlider === "function") {
      window.initUpsellSlider();
    }

    // Émettre un événement
    document.dispatchEvent(new CustomEvent("cart:updated", { detail: data }));
  }

  // Affiche le panier à partir de la dernière réponse (ou relit les sections au besoin)
  async function present(data, state, openDrawer = false) {
    let view = data;
    if (!view?.sections) {
      view = { ...(view || {}), ...((await fetchSections()) || {}) };
    }
    if (typeof view.item_count !== "number") {
      view.item_count = state.reduce((total, line) => total + line.qty, 0);
    }
    renderCart(view, openDrawer);
    syncWholesaleApp();
    return view;
  }

  // L'app de prix de gros (WPD) crée ses commandes à partir de window.wcp_data :
  // on lui redonne le panier à jour après chaque changement
  let wholesaleSync = Promise.resolve();
  function syncWholesaleApp() {
    if (!window.wcp_data) return wholesaleSync;
    wholesaleSync = (async () => {
      try {
        const result = await request(
          `${root}search?view=wcp_cart&_=${Date.now()}`,
          { cache: "no-store" },
        );
        const cart = result.data;
        if (!cart || !Array.isArray(cart.items)) return;
        if (cart.token && cart.token.token) cart.token = cart.token.token;
        window.wcp_data.wcp_cart = cart;
        if (typeof window.wcpRefreshCartTotal === "function") {
          window.wcpRefreshCartTotal();
        }
      } catch (error) {
        logError("Synchronisation WPD impossible:", error.message);
      }
    })();
    return wholesaleSync;
  }

  // === ÉTATS UI ===
  function setLoading(button, loading) {
    if (!button) return;

    button.classList.toggle("loading", loading);
    if (loading) button.setAttribute("aria-disabled", "true");
    else button.removeAttribute("aria-disabled");

    const spinner = button.querySelector(".loading-overlay__spinner");
    if (spinner) {
      spinner.classList.toggle("hidden", !loading);
    }
  }

  function showError(message, nearElement) {
    const inDrawer = nearElement?.closest?.("cart-drawer");
    const el =
      (inDrawer && document.getElementById("CartDrawer-CartErrors")) ||
      document.getElementById("cart-errors") ||
      document.getElementById("CartDrawer-CartErrors");
    if (el) {
      el.textContent = message;
    }
    return !!el;
  }

  // === AJOUTER AU PANIER ===
  async function addNow(entries, showDrawer) {
    const items = entries.map((entry) => ({
      id: parseInt(entry.id, 10),
      quantity: parseInt(entry.quantity, 10) || 1,
      ...(entry.properties && { properties: entry.properties }),
    }));

    const result = await postJson(routes.add, {
      items,
      sections: getSections(),
      sections_url: window.location.pathname,
    });

    if (!result.ok) {
      // Remettre le panier d'aplomb avant de signaler l'erreur
      try {
        const cleanup = await reconcileState();
        await present(cleanup.data, cleanup.state);
      } catch (cleanupError) {
        logError("Réconciliation après échec d'ajout:", cleanupError.message);
      }
      throw new Error(errorMessage(result, "Erreur lors de l'ajout au panier"));
    }

    // Les taxes du produit sont ajoutées ici, à partir du panier relu
    const outcome = await reconcileState();
    const data = await present(
      outcome.changed ? outcome.data : { sections: result.data?.sections },
      outcome.state,
      showDrawer,
    );
    log("✓ Ajouté au panier");
    return data;
  }

  async function add(variantId, quantity = 1, options = {}) {
    const { button, showDrawer = true } = options;
    log("Ajout:", variantId, "x", quantity);

    if (button) setLoading(button, true);

    try {
      const data = await enqueue(() =>
        addNow([{ id: variantId, quantity }], showDrawer),
      );

      // Émettre un événement personnalisé pour notifier que l'ajout est terminé
      document.dispatchEvent(
        new CustomEvent("cart:item-added", {
          detail: { data, variantId, quantity },
        }),
      );

      return data;
    } catch (error) {
      logError("Erreur ajout:", error.message);
      showError(error.message);
      throw error;
    } finally {
      if (button) setLoading(button, false);
    }
  }

  // Ajoute plusieurs variantes en une fois : [{ id, quantity, properties? }]
  async function addItems(entries, options = {}) {
    const { showDrawer = true } = options;
    const data = await enqueue(() => addNow(entries, showDrawer));

    document.dispatchEvent(
      new CustomEvent("cart:item-added", { detail: { data } }),
    );

    return data;
  }

  // === MODIFIER QUANTITÉ ===
  function findLine(state, line) {
    let target = state[parseInt(line, 10) - 1] || null;

    // Sur la page panier, la ligne affichée donne sa variante : on s'assure de viser la bonne
    const row = document.getElementById(`CartItem-${line}`);
    const rowVariantId = parseInt(row?.dataset?.variantId, 10);
    if (rowVariantId && target?.variant_id !== rowVariantId) {
      const matches = state.filter((item) => item.variant_id === rowVariantId);
      target = matches.length === 1 ? matches[0] : null;
    }

    return target;
  }

  async function updateNow(line, quantity) {
    const state = await fetchState();
    const target = findLine(state, line);

    if (!target) {
      // L'affichage ne correspond plus au panier : on le rafraîchit
      const outcome = await reconcileState(state);
      await present(outcome.data, outcome.state);
      throw new Error("Item non trouvé");
    }

    // Les lignes de taxe suivent les produits : une modification directe est annulée
    if (target.tax) {
      const outcome = await reconcileState(state);
      return present(outcome.data, outcome.state);
    }

    // Produit et taxes partent dans la même requête
    const projected = state.map((item) =>
      item === target ? { ...item, qty: quantity } : item,
    );
    const updates = { ...computeFix(projected).updates, [target.key]: quantity };

    const result = await postJson(routes.update, {
      updates,
      sections: getSections(),
      sections_url: window.location.pathname,
    });
    const updated = result.ok && isCart(result.data);

    // Même après un refus (stock insuffisant, etc.), le panier relu est remis d'aplomb
    const outcome = await reconcileState(
      updated ? await buildState(result.data) : null,
    );
    const data = await present(
      updated && !outcome.changed ? result.data : outcome.data,
      outcome.state,
    );

    if (!result.ok) {
      throw new Error(errorMessage(result, "Erreur lors de la mise à jour"));
    }

    log("✓ Quantité mise à jour");
    return data;
  }

  async function update(line, newQuantity) {
    const quantity = parseInt(newQuantity, 10);
    if (!Number.isFinite(quantity) || quantity < 0) {
      throw new Error("Quantite invalide");
    }
    log("Update ligne", line, "→", quantity);

    try {
      return await enqueue(() => updateNow(line, quantity));
    } catch (error) {
      logError("Erreur update:", error.message, { line, newQuantity });
      showError(error.message);
      throw error;
    }
  }

  // === SUPPRIMER ===
  async function remove(line) {
    log("Suppression ligne", line);
    return update(line, 0);
  }

  // === VIDER LE PANIER ===
  async function clear() {
    log("Vidage complet du panier");

    try {
      const result = await postJson(routes.clear, {});

      if (!result.ok) {
        throw new Error("Erreur lors du vidage du panier");
      }

      log("✓ Panier vidé");

      // Recharger la page pour mettre à jour l'affichage
      window.location.reload();

      return result.data;
    } catch (error) {
      logError("Échec vidage panier:", error);
      throw error;
    }
  }

  // === RÉCONCILIATION DES TAXES ===
  // Retourne { ok, changed } : ok = taxes conformes, changed = le panier a été corrigé
  let pendingReconcile = null;
  function reconcileTaxes() {
    if (pendingReconcile) return pendingReconcile;

    const run = enqueue(async () => {
      pendingReconcile = null;
      const outcome = await reconcileState();
      if (outcome.changed) await present(outcome.data, outcome.state);
      return outcome;
    });
    pendingReconcile = run;
    run.catch(() => {
      if (pendingReconcile === run) pendingReconcile = null;
    });
    return run;
  }

  // Diagnostic (console) : pour chaque ligne du panier, la taxe attendue,
  // puis les corrections que la réconciliation ferait. Ne modifie rien.
  async function debugTaxes() {
    const state = await fetchState();
    const fix = computeFix(state);
    console.table(
      state.map(({ key, ...line }) => ({ ...line, key: key.slice(0, 24) })),
    );
    console.table(
      [...fix.expected.values()].map((entry) => ({
        produit: entry.label,
        type: entry.type,
        taxe: entry.taxId,
        attendu: entry.qty,
        present: state
          .filter(
            (line) =>
              line.tax &&
              line.variant_id === entry.taxId &&
              line.taxFor === String(entry.forId),
          )
          .reduce((total, line) => total + line.qty, 0),
      })),
    );
    const report = { state, updates: fix.updates, adds: fix.adds };
    console.log("[Accise] " + JSON.stringify(report));
    return report;
  }

  let reconcileTimer = null;
  let externalChange = false;
  function scheduleReconcile(afterCartRequest = false) {
    externalChange = externalChange || afterCartRequest;
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(async () => {
      const wasExternal = externalChange;
      externalChange = false;
      try {
        const outcome = await reconcileTaxes();
        // Un changement fait hors CartManager : l'app de prix de gros doit le voir aussi
        if (wasExternal && !outcome.changed) syncWholesaleApp();
      } catch (error) {
        logError("[Accise] Réconciliation impossible:", error.message);
      }
    }, 200);
  }

  // Après TOUT changement du panier fait ailleurs (formulaires du thème, apps, jQuery)
  function isCartChange(method, url) {
    return (
      String(method || "GET").toUpperCase() !== "GET" &&
      /\/cart\/(add|change|update)(\.js)?(\?|$)/.test(String(url || ""))
    );
  }

  function watchCartRequests() {
    window.fetch = async function (...args) {
      const response = await nativeFetch(...args);
      try {
        const url = typeof args[0] === "string" ? args[0] : args[0]?.url;
        const method = args[1]?.method || args[0]?.method;
        if (isCartChange(method, url)) scheduleReconcile(true);
      } catch (e) {
        /* ne jamais casser la requête d'origine */
      }
      return response;
    };

    const originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        if (isCartChange(method, url)) {
          this.addEventListener("loadend", () => scheduleReconcile(true));
        }
      } catch (e) {
        /* ne jamais casser la requête d'origine */
      }
      return originalOpen.apply(this, arguments);
    };
  }

  // === DERNIER CONTRÔLE AVANT LE CHECKOUT ===
  let checkoutPassUntil = 0;
  let checkoutChecking = false;

  function withTimeout(promise, ms) {
    return Promise.race([
      promise,
      new Promise((resolve, reject) =>
        setTimeout(() => reject(new Error("Délai dépassé")), ms),
      ),
    ]);
  }

  async function guardCheckout(trigger, proceed) {
    if (checkoutChecking) return;
    checkoutChecking = true;
    setLoading(trigger, true);
    showError("", trigger);

    let outcome = null;
    try {
      outcome = await withTimeout(reconcileTaxes(), CHECKOUT_TIMEOUT_MS);
    } catch (error) {
      // Contrôle impossible (réseau, délai) : on ne retient pas le client
      logError("[Accise] Contrôle pré-checkout impossible:", error.message);
    }

    setLoading(trigger, false);
    checkoutChecking = false;

    if (outcome && !outcome.ok && BLOCK_CHECKOUT_ON_MISMATCH) {
      const message = t(
        "Les taxes d'accise de votre panier n'ont pas pu être mises à jour. Veuillez réessayer ou nous contacter.",
        "The excise taxes in your cart could not be updated. Please try again or contact us.",
      );
      if (!showError(message, trigger)) alert(message);
      return;
    }

    // Panier corrigé à l'instant sur une page gérée par l'app de prix de gros :
    // ses totaux se recalculent, le client confirme avec le panier à jour sous les yeux
    if (outcome?.changed && window.wcp_data) {
      await wholesaleSync;
      const message = t(
        "Votre panier a été mis à jour (taxes d'accise). Veuillez vérifier le total, puis cliquer de nouveau pour passer à la caisse.",
        "Your cart was updated (excise taxes). Please review the total, then click again to check out.",
      );
      if (!showError(message, trigger)) alert(message);
      return;
    }

    checkoutPassUntil = Date.now() + CHECKOUT_PASS_MS;
    proceed();
  }

  function watchCheckout() {
    document.addEventListener(
      "click",
      (event) => {
        const trigger = event.target?.closest?.(CHECKOUT_SELECTOR);
        if (!trigger || Date.now() < checkoutPassUntil) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        guardCheckout(trigger, () => {
          // Le bouton a pu être remplacé par le rafraîchissement du panier
          const current = trigger.isConnected
            ? trigger
            : trigger.id && document.getElementById(trigger.id);
          if (current) current.click();
          else checkoutPassUntil = 0;
        });
      },
      true,
    );

    // Soumission du formulaire sans clic (requestSubmit par un script)
    document.addEventListener(
      "submit",
      (event) => {
        const submitter = event.submitter;
        if (!submitter || submitter.name !== "checkout") return;
        if (Date.now() < checkoutPassUntil) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const form = event.target;
        guardCheckout(submitter, () => {
          if (submitter.isConnected) form.requestSubmit(submitter);
          else checkoutPassUntil = 0;
        });
      },
      true,
    );
  }

  // === INITIALISATION ===
  function init() {
    log("CartManager initialisé");
    scheduleReconcile();
  }

  watchCartRequests();
  watchCheckout();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Retour arrière du navigateur : la page affichée peut dater d'avant un changement
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) scheduleReconcile();
  });

  // === API PUBLIQUE ===
  return {
    add,
    addItems,
    update,
    remove,
    clear,
    getCart,
    reconcileTaxes,
    debugTaxes,
    setDebug: function (val) {
      DEBUG = val;
    },
  };
})();
